/**
 * Sell wizard -- standalone marketplace listing form.
 *
 * Single vertical form (our demographic likes clarity over step cards).
 * Calls POST /api/products. Auto-feature for ADMIN_GRANTED dealers on
 * their first listings is handled backend-side; the UI simply mentions
 * the perk so sellers feel the reward.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { api, parseApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { ImageUploader } from "../../components/ui/ImageUploader";
import { PRODUCT_CONDITIONS } from "../../lib/conditions";
import {
  validateDescription,
  validatePartNumber,
  validatePrice,
  validateTitle,
} from "../../lib/productValidation";
import type { CategoryNode, ProductCondition, ShippingType } from "../../lib/types";

/** Order matches the visual top-to-bottom layout, used to focus the first
 * invalid field on a failed submit (client-side or server-side). */
const FIELD_ORDER = ["title", "part_number", "price", "description"] as const;
type ValidatedField = (typeof FIELD_ORDER)[number];

export default function Sell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [categories, setCategories] = React.useState<CategoryNode[]>([]);
  const [images, setImages] = React.useState<string[]>([]);
  const [fitmentTags, setFitmentTags] = React.useState<string[]>([]);
  const [form, setForm] = React.useState({
    title: "",
    part_number: "",
    description: "",
    price: "",
    condition: "USED" as ProductCondition,
    stock_quantity: "1",
    shipping_type: "BUYER_PAYS" as ShippingType,
    shipping_cost: "",
    category_id: "",
    tags_csv: "",
    compatibility_notes: "",
  });
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState(false);
  const fieldRefs = React.useRef<Partial<Record<ValidatedField, HTMLElement | null>>>({});

  React.useEffect(() => {
    (async () => {
      const r = await api.get<CategoryNode[]>("/categories?tree=true");
      setCategories(r.data);
    })();
  }, []);

  const update = (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));

  const validateOne = React.useCallback(
    (key: ValidatedField): string | undefined => {
      switch (key) {
        case "title":
          return validateTitle(form.title);
        case "part_number":
          return validatePartNumber(form.part_number);
        case "description":
          return validateDescription(form.description);
        case "price":
          return validatePrice(form.price);
        default:
          return undefined;
      }
    },
    [form.title, form.part_number, form.description, form.price],
  );

  const handleBlur = (key: ValidatedField) => () => {
    const msg = validateOne(key);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (msg) next[key] = msg;
      else delete next[key];
      return next;
    });
  };

  const focusField = (key: string) => {
    const el = fieldRefs.current[key as ValidatedField];
    el?.focus();
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const validateAll = (): Record<string, string> => {
    const next: Record<string, string> = {};
    for (const key of FIELD_ORDER) {
      const msg = validateOne(key);
      if (msg) next[key] = msg;
    }
    setFieldErrors(next);
    return next;
  };

  const adminGranted =
    user?.role === "DEALER" && user.subscription_status === "ADMIN_GRANTED";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const clientErrors = validateAll();
    const firstInvalid = FIELD_ORDER.find((k) => clientErrors[k]);
    if (firstInvalid) {
      focusField(firstInvalid);
      return;
    }

    const priceNum = Number(form.price);
    const stockNum = Number(form.stock_quantity);
    const shipNum = Number(form.shipping_cost || "0");
    setLoading(true);
    try {
      const r = await api.post("/products", {
        title: form.title.trim(),
        description: form.description.trim(),
        price: priceNum,
        part_number: form.part_number.trim(),
        condition: form.condition,
        stock_quantity: stockNum,
        shipping_type: form.shipping_type,
        shipping_cost: form.shipping_type === "FREE" ? 0 : shipNum,
        category_id: form.category_id || undefined,
        images,
        tags: form.tags_csv
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        // Phase 15 — fitment fields. Server normalises (trim / lower /
        // dedupe / cap) so we don't pre-process here.
        compatibility_notes: form.compatibility_notes.trim(),
        fitment_tags: fitmentTags,
      });
      navigate(`/product/${r.data.id}`);
    } catch (err) {
      const parsed = parseApiError(err);
      if (Object.keys(parsed.fieldErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...parsed.fieldErrors }));
        const firstServerInvalid = FIELD_ORDER.find((k) => parsed.fieldErrors[k]);
        if (firstServerInvalid) focusField(firstServerInvalid);
      } else {
        setError(parsed.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 md:py-12">
      <header className="mb-6">
        <h1 className="text-3xl md:text-4xl font-black mb-2">List a part</h1>
        <p className="text-ink-muted">
          Fill this in once — your listing appears on the marketplace immediately.
        </p>
      </header>

      {adminGranted && (
        <Card className="mb-6 border-brand" data-testid="sell-perk-banner">
          <CardBody className="flex items-center gap-4">
            <Badge variant="brand">Perk</Badge>
            <p className="text-sm text-ink">
              As an admin-granted dealer, your first 3 listings are{" "}
              <strong>automatically featured for 7 days</strong> at no charge.
            </p>
          </CardBody>
        </Card>
      )}

      <Card elevated>
        <CardHeader>
          <h2 className="text-xl font-bold">Listing details</h2>
        </CardHeader>
        <CardBody>
          <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
            <Input
              label="Listing title"
              required
              value={form.title}
              onChange={update("title")}
              onBlur={handleBlur("title")}
              error={fieldErrors.title}
              ref={(el) => {
                fieldRefs.current.title = el;
              }}
              placeholder="e.g. OEM Yamaha F250 fuel pump"
              data-testid="sell-title"
            />
            <Input
              label="Part number"
              required
              value={form.part_number}
              onChange={update("part_number")}
              onBlur={handleBlur("part_number")}
              error={fieldErrors.part_number}
              ref={(el) => {
                fieldRefs.current.part_number = el;
              }}
              placeholder="67F-44352-00"
              data-testid="sell-partnum"
              hint={
                fieldErrors.part_number
                  ? undefined
                  : "Use the exact OEM / manufacturer number. Buyers search by this."
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Price (USD)"
                type="number"
                min="0"
                step="0.01"
                required
                value={form.price}
                onChange={update("price")}
                onBlur={handleBlur("price")}
                error={fieldErrors.price}
                ref={(el) => {
                  fieldRefs.current.price = el;
                }}
                data-testid="sell-price"
              />
              <Input
                label="Stock quantity"
                type="number"
                min="1"
                required
                value={form.stock_quantity}
                onChange={update("stock_quantity")}
                data-testid="sell-stock"
              />
              <Select
                label="Condition"
                value={form.condition}
                onChange={update("condition")}
                options={PRODUCT_CONDITIONS.map((c) => ({
                  value: c.value,
                  label: c.label,
                }))}
                data-testid="sell-condition"
              />
              <Select
                label="Category"
                value={form.category_id}
                onChange={update("category_id")}
                placeholder="Choose a category (optional)"
                data-testid="sell-category"
              >
                {categories.map((cat) =>
                  cat.children.length === 0 ? (
                    // Childless parent (e.g. General) — valid listing category
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ) : (
                    // Parent with children — non-selectable group header
                    <optgroup key={cat.id} label={cat.name.toUpperCase()}>
                      {cat.children.map((child) => (
                        <option key={child.id} value={child.id}>
                          {child.name}
                        </option>
                      ))}
                    </optgroup>
                  ),
                )}
              </Select>
              <Select
                label="Shipping"
                value={form.shipping_type}
                onChange={update("shipping_type")}
                options={[
                  { value: "FREE", label: "Free (I pay)" },
                  { value: "BUYER_PAYS", label: "Buyer pays" },
                ]}
                data-testid="sell-shipping-type"
              />
              {form.shipping_type === "BUYER_PAYS" && (
                <Input
                  label="Shipping cost (USD)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.shipping_cost}
                  onChange={update("shipping_cost")}
                  data-testid="sell-shipping-cost"
                />
              )}
            </div>

            <div>
              <label
                htmlFor="sell-desc"
                className="mb-1.5 block text-sm font-semibold text-ink"
              >
                Description
              </label>
              <textarea
                id="sell-desc"
                data-testid="sell-description"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                onBlur={handleBlur("description")}
                ref={(el) => {
                  fieldRefs.current.description = el;
                }}
                rows={5}
                required
                aria-invalid={Boolean(fieldErrors.description) || undefined}
                className={`w-full rounded-xl border-2 bg-white px-3 py-3 text-base placeholder:text-ink-subtle focus:outline-none ${
                  fieldErrors.description
                    ? "border-brand focus:border-brand"
                    : "border-surface-border focus:border-ink"
                }`}
                placeholder="Include fit notes, year ranges, condition specifics..."
              />
              {fieldErrors.description && (
                <p
                  data-testid="sell-description-error"
                  className="mt-1.5 text-sm text-brand font-medium"
                >
                  {fieldErrors.description}
                </p>
              )}
            </div>

            {/* Phase 15 — compatibility / fitment ----------------- */}
            <div>
              <label
                htmlFor="sell-compat-notes"
                className="mb-1.5 block text-sm font-semibold text-ink"
              >
                Compatibility / Fits (optional)
              </label>
              <textarea
                id="sell-compat-notes"
                data-testid="sell-compatibility-notes"
                value={form.compatibility_notes}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    compatibility_notes: e.target.value.slice(0, 500),
                  }))
                }
                rows={3}
                maxLength={500}
                placeholder="e.g. Fits Mercury 50hp 1996-2002, Yamaha F50 1998-2003. Leave blank if universal."
                className="w-full rounded-xl border-2 border-surface-border bg-white px-3 py-3 text-base placeholder:text-ink-subtle focus:outline-none focus:border-ink"
              />
              <p className="mt-1 text-xs text-ink-muted">
                {form.compatibility_notes.length}/500 characters
              </p>
            </div>

            <FitmentTagInput
              tags={fitmentTags}
              onChange={setFitmentTags}
              testid="sell-fitment-tags"
            />

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-ink">
                Photos
              </label>
              <ImageUploader
                value={images}
                onChange={setImages}
                max={5}
                testid="sell-images"
              />
            </div>

            <Input
              label="Tags (optional)"
              value={form.tags_csv}
              onChange={update("tags_csv")}
              placeholder="yamaha, fuel-pump, f250"
              hint="Helps buyers find your listing."
              data-testid="sell-tags"
            />

            {error && (
              <div
                role="alert"
                data-testid="sell-error"
                className="rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm font-medium"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
              data-testid="sell-submit"
            >
              Publish listing
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}


/**
 * <FitmentTagInput />
 *
 * Chip-style input. User types a make/model/year string, presses Enter
 * or `,` to add a chip; Backspace on an empty input removes the most
 * recent chip. Disabled at the 20-tag cap so the user can't blow past
 * the server limit. Server still validates so this is purely UX guard.
 */
function FitmentTagInput({
  tags,
  onChange,
  testid,
}: {
  tags: string[];
  onChange: (next: string[]) => void;
  testid: string;
}) {
  const [draft, setDraft] = React.useState("");
  const MAX = 20;
  const full = tags.length >= MAX;

  const commit = (raw: string) => {
    const cleaned = raw.trim().toLowerCase();
    if (!cleaned) return;
    if (cleaned.length > 60) return; // server-side will also reject
    if (tags.includes(cleaned)) {
      setDraft("");
      return;
    }
    if (tags.length >= MAX) return;
    onChange([...tags, cleaned]);
    setDraft("");
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && tags.length > 0) {
      e.preventDefault();
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div data-testid={testid}>
      <label
        htmlFor={`${testid}-input`}
        className="mb-1.5 block text-sm font-semibold text-ink"
      >
        Fitment tags (optional)
      </label>
      <div className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-surface-border bg-white px-3 py-2 min-h-12 focus-within:border-ink">
        {tags.map((t, i) => (
          <span
            key={t}
            data-testid={`${testid}-chip-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-xs font-semibold px-3 py-1.5"
          >
            {t}
            <button
              type="button"
              aria-label={`Remove ${t}`}
              onClick={() => onChange(tags.filter((x) => x !== t))}
              data-testid={`${testid}-remove-${i}`}
              className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white text-[10px] leading-none"
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={`${testid}-input`}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 60))}
          onKeyDown={onKey}
          onBlur={() => commit(draft)}
          disabled={full}
          maxLength={60}
          placeholder={
            full ? `Max ${MAX} tags reached` : "e.g. Mercury 50hp 1998"
          }
          className="flex-1 min-w-[160px] bg-transparent outline-none text-base placeholder:text-ink-subtle disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`${testid}-text-input`}
        />
      </div>
      <p className="mt-1 text-xs text-ink-muted">
        {tags.length}/{MAX} tags · Enter or comma to add · max 60 chars each
      </p>
    </div>
  );
}
